import { isFulfilled, isPending, isRejected, Middleware } from "@reduxjs/toolkit"

import {
  decrementPendingKey,
  incrementPendingKey,
  setLoaderSuppression,
} from "./loaderSuppressionSlice"

/**
 * Watches every dispatched thunk lifecycle action for two optional fields on
 * its original argument object (see clinicalRequestBasicType for an example):
 *
 * - `trackLoaderKey`: just counts this call as pending under that key, via
 *   loaderSuppressionSlice's pendingByKey. Read with selectIsKeyPending and
 *   combine with your own condition (e.g. a route check) before calling
 *   useSuppressGlobalLoader yourself.
 * - `suppressLoaderKey`: unconditionally hides the global loader for the
 *   entire lifetime of this call - no extra wiring needed.
 *
 * Lets any thunk opt in by adding one field to an existing dispatch call,
 * instead of writing a dedicated tracker slice per feature.
 */
export const loaderSuppressionMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    const arg = (action as { meta?: { arg?: unknown } })?.meta?.arg as
      | { trackLoaderKey?: string; suppressLoaderKey?: string }
      | undefined

    if (arg?.trackLoaderKey || arg?.suppressLoaderKey) {
      if (isPending(action)) {
        if (arg.trackLoaderKey) {
          storeApi.dispatch(incrementPendingKey(arg.trackLoaderKey))
        }
        if (arg.suppressLoaderKey) {
          storeApi.dispatch(
            setLoaderSuppression({ key: arg.suppressLoaderKey, isActive: true }),
          )
        }
      } else if (isFulfilled(action) || isRejected(action)) {
        if (arg.trackLoaderKey) {
          storeApi.dispatch(decrementPendingKey(arg.trackLoaderKey))
        }
        if (arg.suppressLoaderKey) {
          storeApi.dispatch(
            setLoaderSuppression({ key: arg.suppressLoaderKey, isActive: false }),
          )
        }
      }
    }

    return next(action)
  }
